/**
 * criteria — «Критерии»: результаты кастомных критериев на звонке (#862).
 *
 * Зеркалит группу «Критерии» панели: те же строки, тот же порядок, те же
 * подписи состояний из `records.analysisPanel.criteria.*`. Данные приходят
 * отдельным полем `input.criteria` — как и соответствие скрипту, они грузятся
 * своим запросом, а не лежат в `analysis.result`.
 *
 * Перехода к моменту в бумаге нет, поэтому цитата печатается текстом. Номер
 * реплики не печатается: на экране он и не показан, а сам по себе, без
 * транскрипта рядом, ничего читателю не говорит.
 *
 * Цвета не назначаются по значению — ровно по той же причине, что в панели:
 * полярность критерия неизвестна, и «сработал» у одного означает нарушение, у
 * другого — выполненный шаг.
 */
import { card, tag } from '../primitives';
import { escapeHtml } from '../escape';
import type { SectionFn, PrintReportInput } from '../types';

type CriterionResult = NonNullable<PrintReportInput['criteria']>[number];

function valueText(item: CriterionResult, t: Parameters<SectionFn>[1]): string {
  if (item.state === 'not_found') {
    return t('records.analysisPanel.criteria.state.notFound', 'Not found in the call');
  }
  if (item.state === 'not_applicable') {
    return t('records.analysisPanel.criteria.state.notApplicable', 'Not applicable');
  }
  if (item.state === 'not_evaluated') {
    return t('records.analysisPanel.criteria.state.notEvaluated', 'Not evaluated');
  }

  const value =
    item.resultType === 'choice'
      ? (item.valueLabel ?? '')
      : item.resultType === 'score'
        ? item.valueScore === null
          ? ''
          : `${item.valueScore}`
        : item.valueBool
          ? t('records.analysisPanel.criteria.yes', 'Yes')
          : t('records.analysisPanel.criteria.no', 'No');

  if (item.triggered === true) {
    return `${value} · ${t('records.analysisPanel.criteria.triggered', 'Triggered')}`;
  }
  return value;
}

export const renderCriteria: SectionFn = (input, t) => {
  const criteria = input.criteria ?? [];
  if (criteria.length === 0) return '';

  const rows = criteria
    .map((item) => {
      // `tag(..., 'muted')` — нейтральная плашка: остальные варианты несут
      // оценку («хорошо»/«плохо»), которой у критерия нет.
      const head =
        `<div class="c1"><span class="n">${escapeHtml(item.name)}</span>` +
        `${tag(valueText(item, t), 'muted')}</div>`;
      const note = item.note ? `<div class="note">${escapeHtml(item.note)}</div>` : '';
      const quotes = item.evidence
        .map((evidence) => `<div class="q">«${escapeHtml(evidence.quote)}»</div>`)
        .join('');
      return `<div class="crt">${head}${note}${quotes}</div>`;
    })
    .join('');

  return card({
    title: t('records.analysisPanel.criteria.title', 'Criteria Results'),
    value: `${criteria.length}`,
    body: rows,
  });
};
